/* eslint-disable */
import React from 'react';
import {
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    makeStyles,
    Typography,
} from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { MinusIcon } from '@phosphor-icons/react';
import { getUserCredential } from 'lib/cookies';
import { shallowEqual, useSelector } from 'react-redux';
import { userInfoSelector } from 'redux/custom-selector';
import { SECONDARY_PLAN_LIST } from 'constants/pricingPlan';
import { getMediaPlusUrl } from 'constants/platform';


const useStyles = makeStyles((theme) => ({
    paper: {
        width: '18.5rem',
        backgroundColor: 'transparent',
        boxShadow: '0 2px 5px 0 var(--Base-Black-100, rgba(0, 0, 0, 0.10)), 0 16px 32px -1px var(--Base-Black-100, rgba(0, 0, 0, 0.10))',
        backdropFilter: 'blur(25px)',
        border: 'none',
    },
    backdrop: {
        backgroundColor: 'transparent',
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: `${theme.spacing(1.5)}px ${theme.spacing(3)}px`,
        minHeight: 72,
    },
    closeBtn: {
        padding: theme.spacing(1.75),
        flexShrink: 0,
        '& svg': {
            color: theme.palette.alert.logoColor,
        }
    },
    navList: {
        display: 'flex',
        flexDirection: 'column',
        paddingTop: `${theme.spacing(1.25)}px`,
        paddingBottom: `${theme.spacing(1.25)}px`,
        gap: `${theme.spacing(1)}px`
    },
    navItem: {
        borderRadius: theme.shape.borderRadius,
        padding: `${theme.spacing(0.75)}px ${theme.spacing(4)}px ${theme.spacing(0.75)}px ${theme.spacing(5)}px`,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    navItemLabel: {
        color: theme.palette.text.grayColor4,
    },
    navItemActive: {
        '& $navItemLabel': {
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
    },
    activeUnderline: {
        height: 2,
        backgroundColor: theme.palette.text.primary,
        marginTop: 6,
        borderRadius: 1,
    },
    activeDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: theme.palette.text.primary,
        flexShrink: 0,
    },
    organiserLogo: {
        maxHeight: 32,
        maxWidth: 71,
        objectFit: 'contain',
    },
}));



const WPlusDrawer = ({ isOpen, onClose }) => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const userInfo = useSelector(userInfoSelector, shallowEqual);
    const { UserInfo, UserInfo: { is_media_plus_enabled = 0 } } = userInfo;
    const isOnlyMediaPlanExits = SECONDARY_PLAN_LIST.includes(UserInfo.planName);
    const organiserLogoUrl = UserInfo?.organiser_logo
        ? `${process.env.REACT_APP_CDN_PATH}/${UserInfo.organiser_logo}`
        : null;
    const isMediaPlan = Boolean(is_media_plus_enabled);
    const { userPermission = {} } = UserInfo;

    const navItems = [
        { label: 'Projects', path: '/events', visible: !isOnlyMediaPlanExits },
        { label: 'EXO', path: '/exo', visible: true },
        {
            label: 'Hubs', path: '/hubs',
            visible: isMediaPlan && (userPermission?.isSuperAdmin || isOnlyMediaPlanExits || userPermission?.canManageMediaPlus),
        },
        {
            label: 'Analytics', path: '/analytics/overview-analytics',
            visible: !isOnlyMediaPlanExits && (userPermission?.canManageAnalytics || userPermission?.isSuperAdmin),
        },
        {
            label: 'Integrations', path: '/integrations',
            visible: userPermission?.canManageIntegrations || userPermission?.isSuperAdmin,
        },
        {
            label: 'Data Mapping', path: '/data-mapping',
            visible: !isOnlyMediaPlanExits && (userPermission?.isSuperAdmin || userPermission?.canManagePtrDataMappings) && Boolean(UserInfo?.is_freeman_customer),
        },
    ];

    const isActive = (path) => {
        if (path === '/events') {
            return location.pathname === '/events' || location.pathname.startsWith('/events/');
        }
        if (path === '/analytics/overview-analytics') {
            return location.pathname.includes('/analytics');
        }
        if (path === '/integrations') {
            return location.pathname.includes('/integrations') || location.pathname.includes('/integration');
        }
        if (path === '/data-mapping') {
            return location.pathname.includes('/data-mapping');
        }
        return location.pathname === path;
    };

    const redirectToMediaPlus = () => {
        const env = process.env.REACT_APP_ENV_TYPE;
        const cookiesData = getUserCredential();
        const url = getMediaPlusUrl();
        window.open(`${url}/validate-token?token=${cookiesData?.authToken}&teamMemberId=${cookiesData?.teamMemberId || 0}`, '_blank');
    };

    const handleNavigate = (path) => {
        if (path === '/hubs') {
            redirectToMediaPlus();
            onClose();
            return;
        }
        if (path === '/analytics/overview-analytics') {
            history.push({
                pathname: path,
                state: { analyticsClicked: true },
            });
            onClose();
            return;
        }
        history.push(path);
        onClose();
    };

    return (
        <Drawer
            anchor="left"
            open={isOpen}
            onClose={onClose}
            classes={{ paper: classes.paper }}
            ModalProps={{ BackdropProps: { classes: { root: classes.backdrop } } }}
        >
            <Box className={classes.drawerHeader}>
                <IconButton className={classes.closeBtn} onClick={onClose} size="small">
                    <MinusIcon width={20} height={20} />
                </IconButton>
                {organiserLogoUrl ? (
                    <img src={organiserLogoUrl} alt="logo" className={classes.organiserLogo} />
                ) : (
                    null
                )}
            </Box>

            <List className={classes.navList} disablePadding>
                {navItems.filter(item => item.visible).map((item) => {
                    const active = isActive(item.path);
                    return (
                        <ListItem
                            key={item.label}
                            className={`${classes.navItem} ${active ? classes.navItemActive : ''}`}
                            onClick={() => handleNavigate(item.path)}
                            disableGutters
                        >
                            <ListItemText
                                primary={
                                    <Box display="inline-flex" flexDirection="column">
                                        <Typography variant='h4' component="span" className={classes.navItemLabel}>
                                            {item.label}
                                        </Typography>
                                        {active && <Box className={classes.activeUnderline} />}
                                    </Box>
                                }
                            />
                            {active && <Box className={classes.activeDot} />}
                        </ListItem>
                    );
                })}
            </List>
        </Drawer>
    );
};

export default WPlusDrawer;
